import request from '@wearetla/tla-essentials-tools/utilities/request';
import kebabCase from 'lodash/kebabCase';
import omit from 'lodash/omit';
import config from '@wearetla/tla-essentials-tools/config';
import { createI18nInstance } from '/utilities/i18n';

const getT = () => createI18nInstance().t;

const siteServices = {
	getAllBranches: () => {
		return new Promise((resolve, reject) => {
			request.get('client/all', false, (payload, status, request) => {
				if (payload?.client_branches?.length) {
					resolve(payload.client_branches);
				} else {
					reject(request);
				}
			});
		});
	},
	getCityVenues: () => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(
				`venue/list-by-city`,
				{},
				(payload, status, request) => {
					if (payload?.cities) {
						const cities = payload.cities.map(city => ({
							...city,
							label: city.city,
							value: city.slug,
							venues: city.venues.map(venue => ({
								...venue,
								label: venue.title,
								value: venue.slug,
							}))
						}));

						resolve({
							cities,
							venues: cities.reduce((venues, city) => {
								return [
									...venues,
									...city.venues.map(venue => ({
										...venue,
										city,
									}))
								]
							}, [])
						});
					} else {
						reject(
							request?.data?.meta?.messages || t('Konumlar getirilirken bir hata ile karşılaşıldı.')
						);
					}
				}
			);
		});
	},
	getLocations: () => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(
				`client/locations`,
				{},
				(payload, status, request) => {
					if (payload?.locations) {
						resolve(payload.locations.map((location) => ({
              city: location,
              shortTitle: location,
              title: location,
              slug: kebabCase(location),
              id: kebabCase(location),
              label: location,
              value: kebabCase(location),
            })));
					} else {
						reject(
							request?.data?.meta?.messages || t('Konumlar getirilirken bir hata ile karşılaşıldı.')
						);
					}
				}
			);
		});
	},
	// Showcases & Banners
	getShowcase: (showcaseType, rawFilters) => {
		const filters = Object.keys(rawFilters ?? {}).reduce((newFilters, key) => {
			if (rawFilters[key]) {
				return {
					...newFilters,
					[key]: rawFilters[key],
				};
			} else {
				return newFilters;
			}
		}, {});
		return new Promise((resolve, reject) => {
			request.get(
				'event/showcase',
				{ showcase_type: showcaseType, ...(filters ?? {}) },
				(payload, status, request) => {
					if (payload?.showcases?.length) {
						resolve(payload.showcases[0]);
					} else {
						reject(request);
					}
				}
			);
		});
	},
	getBanners: (branchId) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(
				`client/${config.clientId}/branch/${branchId}/banners`,
				{},
				(payload, status, request) => {
					if (payload?.client_branch_banners) {
						resolve(payload.client_branch_banners);
					} else {
						reject(
							request?.data?.meta?.messages || t('Bannerlar getirilirken bir hata ile karşılaşıldı.')
						);
					}
				}
			);
		});
	},
	// Events
	getEventCategories: () => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(
				`event/types`,
				{},
				(payload, status, request) => {
					if (payload?.event_types) {
						resolve(payload.event_types.map(type => ({
              ...type,
              value: type.id.toString(),
              label: type.name,
            })));
					} else {
						reject(
							request?.data?.meta?.messages || t('Kategoriler getirilirken bir hata ile karşılaşıldı.')
						);
					}
				}
			);
		});
	},
	// Faqs
	getFaqCategories: () => {
		return new Promise((resolve, reject) => {
			request.get('client/faq', false, (payload, status, request) => {
				if (payload?.faqs) {
					resolve(
						payload.faqs.map((faq) => ({
							title: faq.title,
							id: faq.id,
							slug: kebabCase(faq.title),
						}))
					);
				} else {
					reject(request);
				}
			});
		});
	},
	getFaqDetail: (faqId) => {
		return new Promise((resolve, reject) => {
			request.get('client/faq', false, (payload, status, request) => {
				if (payload?.faqs) {
					resolve(payload.faqs.find((f) => f.id.toString() === faqId));
				} else {
					reject(request);
				}
			});
		});
	},
	// Pages
	getPageCategories: () => {
		return new Promise((resolve, reject) => {
			request.get('client/content', false, (payload, status, request) => {
				if (payload?.contents) {
					resolve(
						payload.contents.map((content) => ({
							title: content.title,
							id: content.id,
							slug: kebabCase(content.title),
						}))
					);
				} else {
					reject(request);
				}
			});
		});
	},
	getPageDetail: (contentId) => {
		return new Promise((resolve, reject) => {
			request.get('client/content', false, (payload, status, request) => {
				if (payload?.contents) {
					const foundContents = payload.contents.find((f) => f.id.toString() === contentId);
					if (foundContents) {
						resolve({
							...omit(foundContents, ['contents']),
							description: foundContents?.description,
						});
					} else {
						reject(request);
					}
				} else {
					reject(request);
				}
			});
		});
	},
	// Contact Form
	getContactSubjects: (branchId) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(
				'client/contact/subjects',
				{ branch_id: branchId },
				(payload, status, request) => {
					if (status === 200 && payload?.contact_subjects) {
						resolve(
							payload.contact_subjects.map((subject) => ({
								...subject,
								value: subject.key,
								label: subject.value,
							}))
						);
					} else {
						reject(
							request?.data?.meta?.messages ||
								t('Konular getirilirken bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
						);
					}
				}
			);
		});
	},
	submitContactForm: (formData) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.post(
				'client/contact',
				{
					...formData,
				},
				(payload, status, request) => {
					if (status === 200) {
						resolve(
							request?.data?.meta?.messages || { success: t('Mesajınız başarıyla gönderildi.') }
						);
					} else {
						reject(
							request?.data?.meta?.messages ||
								t('Mesajınız gönderilirken bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
						);
					}
				}
			);
		});
	},
	// Menu
	getAllMenu: () => {
		return new Promise((resolve, reject) => {
			request.get('menu/all-products', false, (payload, status, request) => {
				if (payload?.all_products) {
					resolve(payload.all_products);
				} else {
					reject(request);
				}
			});
		});
	},
	getBranchMenu: (branchSlug) => {
		return new Promise((resolve, reject) => {
			request.get(`menu/branch/${branchSlug}`, false, (payload, status, request) => {
				if (payload?.menu) {
					resolve({
						...payload.menu,
						merchants: (
							payload.menu?.merchants?.filter?.(
								(merchant) => !!merchant.menus.filter((menu) => !!menu.products.length).length
							) ?? []
						).sort((a, b) => (a?.rank ?? 0) - (b?.rank ?? 0)),
					});
				} else {
					reject(request);
				}
			});
		});
	},
	// Forms
	getCustomForm: (formId) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.get(`form/${formId}`, false, (payload, status, request) => {
				if (status === 200 && payload?.form) {
					resolve(payload.form);
				} else {
					reject(
						request?.data?.meta?.messages ||
							t('Form getirilirken bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
					);
				}
			});
		});
	},
	submitCustomForm: (formId, fields) => {
		const t = getT();
		return new Promise((resolve, reject) => {
			request.post(
				`form/${formId}/answer`,
				{
					...fields,
				},
				(payload, status, request) => {
				if (status === 200) {
					resolve(
						request?.data?.meta?.messages?.success?.length
							? request?.data?.meta?.messages
							: { success: t('Form başarıyla iletildi, teşekkür ederiz.') }
					);
				} else {
					reject(
						request?.data?.meta?.messages ||
							t('Form gönderilirken bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
					);
				}
			});
		});
	},
	resetCustomForm: (formId) => {
		const t = getT();

		return new Promise((resolve, reject) => {
			request.delete(`form/${formId}/answer`, false, (payload, status, request) => {
				if (status === 200) {
					resolve(
						request?.data?.meta?.messages?.success?.length
							? request?.data?.meta?.messages
							: { success: t('Form başarıyla iletildi, teşekkür ederiz.') }
					);
				} else {
					reject(
						request?.data?.meta?.messages ||
							t('Form gönderilirken bir hata ile karşılaşıldı, lütfen tekrar deneyin.')
					);
				}
			});
		});
	},
};

export default siteServices;
